import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

const VACCINE_TYPES = [
  { key: 'covid_19', label: 'COVID-19' },
  { key: 'flu', label: 'Flu (Influenza)' },
  { key: 'hpv', label: 'HPV' },
  { key: 'hepatitis_a', label: 'Hepatitis A' },
  { key: 'hepatitis_b', label: 'Hepatitis B' },
  { key: 'tdap', label: 'Tdap' },
  { key: 'mmr', label: 'MMR' },
  { key: 'varicella', label: 'Varicella' },
  { key: 'meningococcal', label: 'Meningococcal' },
  { key: 'other', label: 'Other' },
];

const styles = {
  container: { padding: '0' },
  info: {
    background: 'var(--accent-soft)',
    borderRadius: 'var(--radius-sm)',
    padding: '12px 16px',
    fontSize: '13px',
    color: 'var(--accent)',
    marginBottom: '16px',
    lineHeight: 1.5,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto auto',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid var(--border-color)',
  },
  label: { fontWeight: '500', fontSize: '14px' },
  radios: {
    display: 'flex',
    gap: '8px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
};

export default function VaccinationForm() {
  const { user, refreshUser } = useAuth();
  const [vaccinations, setVaccinations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.vaccinations) {
      const vaxMap = {};
      for (const v of user.vaccinations) {
        vaxMap[v.vaccine_type] = v;
      }
      setVaccinations(
        VACCINE_TYPES.map((vt) => ({
          vaccine_type: vt.key,
          status: vaxMap[vt.key]?.status || 'not_vaccinated',
          visibility: vaxMap[vt.key]?.visibility || 'public',
        }))
      );
    } else {
      setVaccinations(
        VACCINE_TYPES.map((vt) => ({
          vaccine_type: vt.key,
          status: 'not_vaccinated',
          visibility: 'public',
        }))
      );
    }
  }, [user]);

  const update = (index, field, value) => {
    setVaccinations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/vaccinations', { vaccinations });
      await refreshUser();
      setSuccess('Vaccination status saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.info}>
        Manage your vaccination disclosures. <strong>Public</strong> = shown on your profile.{' '}
        <strong>Private</strong> = used for matching filters only.{' '}
        <strong>Opted out</strong> = not disclosed.
      </div>

      {VACCINE_TYPES.map((vt, idx) => (
        <div key={vt.key} style={styles.row}>
          <span style={styles.label}>{vt.label}</span>

          <div style={styles.radios}>
            {['vaccinated', 'partial', 'not_vaccinated'].map((s) => (
              <label key={s} style={styles.radioLabel}>
                <input
                  type="radio"
                  name={`vax-status-${vt.key}`}
                  checked={vaccinations[idx]?.status === s}
                  onChange={() => update(idx, 'status', s)}
                  style={{ width: 'auto' }}
                />
                {s === 'vaccinated' ? 'Yes' : s === 'partial' ? 'Partial' : 'No'}
              </label>
            ))}
          </div>

          <div style={styles.radios}>
            {['public', 'private', 'opted_out'].map((v) => (
              <label key={v} style={styles.radioLabel}>
                <input
                  type="radio"
                  name={`vax-vis-${vt.key}`}
                  checked={vaccinations[idx]?.visibility === v}
                  onChange={() => update(idx, 'visibility', v)}
                  style={{ width: 'auto' }}
                />
                {v === 'public' ? 'Public' : v === 'private' ? 'Private' : 'Opt out'}
              </label>
            ))}
          </div>
        </div>
      ))}

      {error && <p className="error-text" style={{ marginTop: '12px' }}>{error}</p>}
      {success && (
        <p style={{ color: 'var(--success)', fontSize: '13px', marginTop: '12px' }}>
          {success}
        </p>
      )}

      <div style={styles.actions}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save vaccinations'}
        </button>
      </div>
    </div>
  );
}
